import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, CornerDownLeft, LayoutGrid, LogOut, Moon, Search, SlidersHorizontal, Sun } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/lib/theme";
import { NAV_GROUPS } from "./nav";
import { SETTINGS } from "./settingsIndex";
import { confirmDiscard } from "@/lib/unsaved";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function CommandPaletteButton({ onClick }) {
  return (
    <button data-testid="command-palette-open" onClick={onClick} aria-label="Search the dashboard"
      className="flex items-center gap-2 h-9 rounded-lg border border-zinc-800 px-2.5 md:pl-3 md:pr-2 text-sm text-zinc-500 hover:text-white hover:bg-zinc-900 transition-colors">
      <Search className="w-4 h-4" />
      <span className="hidden md:inline">Search…</span>
      <kbd className="hidden md:inline text-[10px] font-mono border border-zinc-800 rounded px-1.5 py-0.5">{isMac ? "⌘" : "Ctrl"} K</kbd>
    </button>
  );
}

export function CommandPalette({ open, setOpen, guildId, onLogout }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const isAdmin = user?.role === "admin" || user?.staff_role;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const run = (fn) => { setOpen(false); fn(); };
  const go = (to) => run(() => confirmDiscard() && navigate(`/app/g/${guildId}${to ? `/${to}` : ""}`));
  const pages = NAV_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <DialogTitle className="sr-only">Search the dashboard</DialogTitle>
      <CommandInput data-testid="command-palette-input" placeholder="Search pages, settings and actions…" />
      <CommandList className="max-h-[60vh]" data-testid="command-palette-list">
        <CommandEmpty data-testid="command-palette-empty">No matches found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {pages.map((p) => (
            <CommandItem key={`p-${p.to}`} value={`${p.label} ${p.group} page`} onSelect={() => go(p.to)} data-testid={`cmd-page-${slug(p.label)}`} className="gap-3 cursor-pointer">
              <p.icon className="text-zinc-500" />
              <span>{p.label}</span>
              <span className="ml-auto text-xs text-zinc-500">{p.group}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Settings">
          {pages.flatMap((p) => (SETTINGS[p.to] || []).map((s) => (
            <CommandItem key={`s-${p.to}-${s}`} value={`${s} ${p.label} setting`} onSelect={() => go(p.to)} data-testid={`cmd-setting-${slug(p.to || "overview")}-${slug(s)}`} className="gap-3 cursor-pointer">
              <SlidersHorizontal className="text-zinc-500" />
              <span>{s}</span>
              <span className="ml-auto text-xs text-zinc-500 flex items-center gap-1">{p.label} <CornerDownLeft className="!w-3 !h-3" /></span>
            </CommandItem>
          )))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Quick actions">
          <CommandItem value="toggle theme dark light mode" onSelect={() => run(toggle)} data-testid="cmd-action-theme" className="gap-3 cursor-pointer">
            {theme === "light" ? <Moon className="text-zinc-500" /> : <Sun className="text-zinc-500" />} Switch to {theme === "light" ? "dark" : "light"} mode
          </CommandItem>
          <CommandItem value="all servers switch server" onSelect={() => run(() => confirmDiscard() && navigate("/app"))} data-testid="cmd-action-servers" className="gap-3 cursor-pointer">
            <LayoutGrid className="text-zinc-500" /> All servers
          </CommandItem>
          {isAdmin && (
            <CommandItem value="owner console admin" onSelect={() => run(() => confirmDiscard() && navigate("/admin"))} data-testid="cmd-action-admin" className="gap-3 cursor-pointer">
              <Crown className="text-zinc-500" /> Open Owner Console
            </CommandItem>
          )}
          <CommandItem value="log out sign out" onSelect={() => run(onLogout)} data-testid="cmd-action-logout" className="gap-3 cursor-pointer text-red-400">
            <LogOut /> Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
